import React, { useState, useEffect } from "react";
import axios from "axios";
import proxy from "../../proxy";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  Input,
  VStack,
  useToast,
  Select,
  HStack,
  Radio,
  RadioGroup,
  Text
} from "@chakra-ui/react";

const TransactionModal = ({ isOpen, onClose, transaction, onSave }) => {
  const [formData, setFormData] = useState({
    from_add: "",
    to_add: "",
    amount: "",
    symbol_crypto: "",
    out_in: "0", // 0 = inflow, 1 = outflow
    date_operation: "",
  });
  const [isLoading, setIsLoading] = useState(false);
  const toast = useToast();

  useEffect(() => {
    if (transaction) {
      setFormData({
        from_add: transaction.from_add || "",
        to_add: transaction.to_add || "",
        amount: transaction.amount || "",
        symbol_crypto: transaction.symbol_crypto || "",
        out_in: String(transaction.out_in) || "1",
        date_operation: transaction.date_operation || "",
      });
    } else {
      // Default to today's date
      const today = new Date().toISOString().split("T")[0];
      setFormData({
        from_add: "",
        to_add: "",
        amount: "",
        symbol_crypto: "",
        out_in: "1",
        date_operation: today,
      });
    }
  }, [transaction, isOpen]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async () => {
    // Validation
    if (!formData.amount || !formData.symbol_crypto || !formData.date_operation) {
      toast({
        title: "Missing fields",
        description: "Amount, Symbol, and Date are required",
        status: "warning",
        duration: 3000,
        position: "top",
      });
      return;
    }

    setIsLoading(true);

    try {
      if (transaction) {
        // Update
        await axios.post(`${proxy}server_mts/admin/UpdateOperation.php`, {
          id: transaction.id,
          ...formData,
        });
        toast({
          title: "Transaction updated successfully",
          status: "success",
          duration: 2000,
          position: "top",
        });
      } else {
        // Add
        await axios.post(`${proxy}server_mts/admin/AddOperation.php`, formData);
        toast({
          title: "Transaction added successfully",
          status: "success",
          duration: 2000,
          position: "top",
        });
      }

      onSave();
      setTimeout(() => {
        onClose();
      }, 100);
    } catch (error) {
      toast({
        title: "Error saving transaction",
        description: error.message,
        status: "error",
        duration: 3000,
        position: "top",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalOverlay bg="blackAlpha.700" backdropFilter="blur(10px)" />
      <ModalContent bg="gray.800" borderColor="whiteAlpha.200" border="1px solid">
        <ModalHeader color="white">
          {transaction ? "Edit Transaction" : "Add New Transaction"}
        </ModalHeader>
        <ModalCloseButton color="gray.400" />
        <ModalBody>
          <VStack spacing={4}>
            {/* Type Selection */}
            <FormControl isRequired>
              <FormLabel color="gray.300" fontSize="sm">
                Transaction Type
              </FormLabel>
              <RadioGroup
                value={formData.out_in}
                onChange={(value) =>
                  setFormData({ ...formData, out_in: value })
                }
              >
                <HStack spacing={6}>
                  <Radio value="1" colorScheme="green">
                    <Text color="green.300" fontSize="sm">
                      ↑ Inflow (Received)
                    </Text>
                  </Radio>
                  <Radio value="0" colorScheme="red">
                    <Text color="red.300" fontSize="sm">
                      ↓ Outflow (Sent)
                    </Text>
                  </Radio>
                </HStack>
              </RadioGroup>
            </FormControl>

            {/* Amount */}
            <FormControl isRequired>
              <FormLabel color="gray.300" fontSize="sm">
                Amount
              </FormLabel>
              <Input
                name="amount"
                type="number"
                step="0.00000001"
                value={formData.amount}
                onChange={handleChange}
                placeholder="0.00000000"
                bg="whiteAlpha.100"
                border="1px solid"
                borderColor="whiteAlpha.300"
                color="white"
                _hover={{ borderColor: "whiteAlpha.400" }}
                _focus={{ borderColor: "teal.400" }}
              />
            </FormControl>

            {/* Symbol */}
            <FormControl isRequired>
              <FormLabel color="gray.300" fontSize="sm">
                Asset Symbol
              </FormLabel>
              <Select
                name="symbol_crypto"
                value={formData.symbol_crypto}
                onChange={handleChange}
                bg="whiteAlpha.100"
                border="1px solid"
                borderColor="whiteAlpha.300"
                color="white"
                _hover={{ borderColor: "whiteAlpha.400" }}
                _focus={{ borderColor: "teal.400" }}
              >
                <option value="" style={{ background: "#2D3748" }}>
                  Select asset
                </option>
                <option value="LTC" style={{ background: "#2D3748" }}>
                  LTC (Litecoin)
                </option>
                <option value="BCH" style={{ background: "#2D3748" }}>
                  BCH (Bitcoin Cash)
                </option>
                <option value="DOGE" style={{ background: "#2D3748" }}>
                  DOGE (Dogecoin)
                </option>
                <option value="DASH" style={{ background: "#2D3748" }}>
                  DASH
                </option>
                <option value="POL" style={{ background: "#2D3748" }}>
                  POL (Polygon)
                </option>
                <option value="ZEC" style={{ background: "#2D3748" }}>
                  ZEC (Zcash)
                </option>
              </Select>
            </FormControl>

            {/* Date */}
            <FormControl isRequired>
              <FormLabel color="gray.300" fontSize="sm">
                Transaction Date
              </FormLabel>
              <Input
                name="date_operation"
                type="date"
                value={formData.date_operation}
                onChange={handleChange}
                bg="whiteAlpha.100"
                border="1px solid"
                borderColor="whiteAlpha.300"
                color="white"
                _hover={{ borderColor: "whiteAlpha.400" }}
                _focus={{ borderColor: "teal.400" }}
              />
            </FormControl>

            {/* From Address (optional) */}
            <FormControl>
              <FormLabel color="gray.300" fontSize="sm">
                From Address (optional)
              </FormLabel>
              <Input
                name="from_add"
                value={formData.from_add}
                onChange={handleChange}
                placeholder="Enter sender address"
                bg="whiteAlpha.100"
                border="1px solid"
                borderColor="whiteAlpha.300"
                color="white"
                fontFamily="monospace"
                fontSize="sm"
                _hover={{ borderColor: "whiteAlpha.400" }}
                _focus={{ borderColor: "teal.400" }}
              />
            </FormControl>

            {/* To Address (optional) */}
            <FormControl>
              <FormLabel color="gray.300" fontSize="sm">
                To Address (optional)
              </FormLabel>
              <Input
                name="to_add"
                value={formData.to_add}
                onChange={handleChange}
                placeholder="Enter recipient address"
                bg="whiteAlpha.100"
                border="1px solid"
                borderColor="whiteAlpha.300"
                color="white"
                fontFamily="monospace"
                fontSize="sm"
                _hover={{ borderColor: "whiteAlpha.400" }}
                _focus={{ borderColor: "teal.400" }}
              />
            </FormControl>
          </VStack>
        </ModalBody>

        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose}>
            Cancel
          </Button>
          <Button
            colorScheme="teal"
            onClick={handleSubmit}
            isLoading={isLoading}
          >
            {transaction ? "Update" : "Add"} Transaction
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default TransactionModal;